/**
 * useStrategyPerformance
 *
 * Fetches and provides performance data for a single strategy program page.
 * Replaces hardcoded performance stats and hero taglines.
 *
 * Usage:
 *   const { performance, loading, error } = useStrategyPerformance('strand-q');
 *
 * @param {string} strategy - CMS slug: 'strand-q' | 'merin' | 'dravon'
 *
 * Returns:
 * {
 *   loading: boolean,
 *   error: string | null,
 *   performance: {
 *     lastUpdated: string,                    // e.g. "2026-03"
 *     hero: { tagline: string },              // hero section tagline
 *     performanceStats: [{ label, value }]    // array of 6 stat objects
 *   } | null
 * }
 */

import { useState, useEffect } from 'react';

import { getStrategyPerformance } from '../services/cmsService';

const useStrategyPerformance = strategy => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performance, setPerformance] = useState(null);

  useEffect(() => {
    if (!strategy) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getStrategyPerformance(strategy);

        if (cancelled) return;

        setPerformance(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load strategy performance');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [strategy]);

  return { loading, error, performance };
};

export default useStrategyPerformance;
